import React, { useEffect, useRef } from 'react';

const ParticleRing = ({ 
  targetRadiusMultiplier = 1.0, 
  flareIntensity = 0, 
  flareColor = [220, 20, 20], 
  isSuccess = false 
}) => {
  const canvasRef = useRef(null);
  
  // Use a ref to store updated props without re-triggering the useEffect
  const latestProps = useRef({
    targetRadiusMultiplier,
    flareIntensity,
    flareColor,
    isSuccess
  });

  // Sync props to ref
  useEffect(() => {
    latestProps.current = {
      targetRadiusMultiplier,
      flareIntensity,
      flareColor,
      isSuccess
    };
  }, [targetRadiusMultiplier, flareIntensity, flareColor, isSuccess]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    
    // PERMANENT STATE: These must never be reset during the session
    let width, height, nodes = [];
    let currentRInner = 300; 
    let currentROuter = 440;
    let nodeCount = 342; 
    let connectionThreshold = 95;
    let globalRotation = Math.random() * Math.PI * 2; // Randomized start
    let pulsePhase = 0;
    let animationFrameId;
    let empRecovery = 1.0;
    let lastTimestamp = 0;
    
    let currentRadiusMultiplier = 1.0;
    let successVel = 0.05;

    const lerpColor = (r1, g1, b1, r2, g2, b2, amount) => {
      const r = Math.round(r1 + (r2 - r1) * amount);
      const g = Math.round(g1 + (g2 - g1) * amount);
      const b = Math.round(b1 + (b2 - b1) * amount);
      return `${r}, ${g}, ${b}`;
    };

    const triggerEMP = () => {
      empRecovery = 0.1;
      const card = document.getElementById('login-card');
      if (card) {
        card.classList.add('emp-blackout');
        setTimeout(() => {
          card.classList.remove('emp-blackout');
        }, 50);
      }
    };

    const handleContextMenu = (e) => {
      e.preventDefault();
      triggerEMP();
    };

    class Node {
      constructor() {
        this.reset();
      }

      reset() {
        this.angle = Math.random() * Math.PI * 2;
        this.baseRadius = currentRInner + Math.random() * (currentROuter - currentRInner);
        this.radius = this.baseRadius;
        this.size = Math.random() * 1.5 + 0.5;
        this.noiseX = Math.random() * 100;
        this.noiseY = Math.random() * 100;
        this.speed = (0.01 + Math.random() * 0.02) * 0.15;
        this.x = 0;
        this.y = 0;
        this.connectionCount = 0;
        this.isHub = false;
      }

      update(pulse) {
        this.radius = (this.baseRadius + (pulse * 18)) * currentRadiusMultiplier;
        this.noiseX += this.speed;
        this.noiseY += this.speed;
        const offsetX = Math.sin(this.noiseX) * 10;
        const offsetY = Math.cos(this.noiseY) * 10;
        const currentAngle = this.angle + globalRotation;
        this.x = Math.cos(currentAngle) * this.radius + offsetX;
        this.y = Math.sin(currentAngle) * this.radius + offsetY;
      }

      draw() {
        // Read latest props for rendering
        const { flareColor, flareIntensity } = latestProps.current;
        const posX = this.x + width / 2;
        const posY = this.y + height / 2;
        const displaySize = this.isHub ? this.size * 1.8 * empRecovery : this.size * empRecovery;

        if (this.isHub) {
          const gradient = ctx.createRadialGradient(
            posX - displaySize * 0.3, 
            posY - displaySize * 0.3, 
            displaySize * 0.05, 
            posX, 
            posY, 
            displaySize
          );
          
          const hubMid = lerpColor(255, 255, 255, flareColor[0], flareColor[1], flareColor[2], flareIntensity);
          const hubHigh = lerpColor(255, 255, 255, flareColor[0], flareColor[1], flareColor[2], flareIntensity * 0.5);
          const hubLow = lerpColor(160, 160, 160, flareColor[0], flareColor[1], flareColor[2], flareIntensity);
          
          gradient.addColorStop(0, `rgba(${hubHigh}, ${0.95 * empRecovery})`);
          gradient.addColorStop(0.2, `rgba(${hubHigh}, ${0.95 * empRecovery})`);
          gradient.addColorStop(0.5, `rgba(${hubMid}, ${0.9 * empRecovery})`);
          gradient.addColorStop(1, `rgba(${hubLow}, ${0.75 * empRecovery})`);
          
          ctx.fillStyle = gradient;
        } else {
          const color = lerpColor(255, 255, 255, flareColor[0], flareColor[1], flareColor[2], flareIntensity);
          ctx.fillStyle = `rgba(${color}, ${0.8 * empRecovery})`;
        }

        ctx.beginPath();
        ctx.arc(posX, posY, displaySize, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    const updateHubs = () => {
      nodes.forEach(n => n.isHub = false);
      const numHubs = Math.floor(nodeCount * 0.1);
      const sliceAngle = (Math.PI * 2) / numHubs;

      nodes.forEach(n => n.connectionCount = 0);
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const dx = nodes[i].x - nodes[j].x, dy = nodes[i].y - nodes[j].y;
          if (dx*dx+dy*dy < connectionThreshold*connectionThreshold) {
            nodes[i].connectionCount++;
            nodes[j].connectionCount++;
          }
        }
      }

      for (let i = 0; i < numHubs; i++) {
        const start = i * sliceAngle, end = (i + 1) * sliceAngle;
        let bestHub = null, maxConnections = -1;
        nodes.forEach(n => {
          let normAngle = n.angle % (Math.PI * 2);
          if (normAngle < 0) normAngle += (Math.PI * 2);
          if (normAngle >= start && normAngle < end) {
            if (n.connectionCount > maxConnections) {
              maxConnections = n.connectionCount;
              bestHub = n;
            }
          }
        });
        if (bestHub) bestHub.isHub = true;
      }
    };

    const resize = () => {
      width = canvas.width = window.innerWidth;
      height = canvas.height = window.innerHeight;

      const isMobile = width < 768;
      if (isMobile) {
        currentRInner = width * 0.35;
        currentROuter = width * 0.55;
        nodeCount = 180;
        connectionThreshold = 65;
      } else {
        currentRInner = 300;
        currentROuter = 440;
        nodeCount = 342;
        connectionThreshold = 95;
      }

      if (nodes.length !== nodeCount) {
        nodes = [];
        for (let i = 0; i < nodeCount; i++) nodes.push(new Node());
        updateHubs();
      } else {
        nodes.forEach(n => {
          n.baseRadius = currentRInner + Math.random() * (currentROuter - currentRInner);
        });
      }
    };

    const drawConnections = () => {
      const { flareColor, flareIntensity } = latestProps.current;
      ctx.lineWidth = 0.5;
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const dx = nodes[i].x - nodes[j].x, dy = nodes[i].y - nodes[j].y, distSq = dx * dx + dy * dy;
          if (distSq < connectionThreshold * connectionThreshold) {
            const dist = Math.sqrt(distSq), opacity = (1 - dist / connectionThreshold) * 0.35 * empRecovery;
            ctx.beginPath();
            ctx.moveTo(nodes[i].x + width / 2, nodes[i].y + height / 2);
            ctx.lineTo(nodes[j].x + width / 2, nodes[j].y + height / 2);
            const color = lerpColor(255, 255, 255, flareColor[0], flareColor[1], flareColor[2], flareIntensity);
            ctx.strokeStyle = `rgba(${color}, ${opacity})`;
            ctx.stroke();
          }
        }
      }
    };

    const init = () => {
      resize();
    };

    const animate = (timestamp) => {
      if (!lastTimestamp) lastTimestamp = timestamp;
      const deltaTime = timestamp - lastTimestamp;
      lastTimestamp = timestamp;

      ctx.clearRect(0, 0, width, height);

      const { isSuccess, targetRadiusMultiplier } = latestProps.current;
      
      // Cap timeFactor to prevent physics explosions when returning from background tabs
      const timeFactor = Math.min(deltaTime / 16.6667, 3.0);

      if (empRecovery < 1.0) {
        empRecovery += (deltaTime / 2500);
        if (empRecovery > 1.0) empRecovery = 1.0;
      }
      
      if (isSuccess) {
        currentRadiusMultiplier += successVel * timeFactor;
        successVel *= Math.pow(0.99, timeFactor);
      } else {
        // Safe lerping
        currentRadiusMultiplier += (targetRadiusMultiplier - currentRadiusMultiplier) * 0.1 * (timeFactor > 1 ? 1 : timeFactor);
      }

      // Normalized Rotation: Target 0.00054 at 60FPS
      globalRotation += (isSuccess ? (0.00054 * currentRadiusMultiplier) : 0.00054) * timeFactor;
      
      pulsePhase += 0.003 * timeFactor;
      const pulse = Math.sin(pulsePhase);
      nodes.forEach(node => node.update(pulse));
      drawConnections();
      nodes.forEach(node => node.draw());
      animationFrameId = requestAnimationFrame(animate);
    };

    window.addEventListener('resize', resize);
    window.addEventListener('contextmenu', handleContextMenu);
    init();
    requestAnimationFrame(animate);

    return () => {
      window.removeEventListener('resize', resize);
      window.removeEventListener('contextmenu', handleContextMenu);
      cancelAnimationFrame(animationFrameId);
    };
  }, []); // Run ONCE on mount

  return <canvas ref={canvasRef} className="absolute inset-0 pointer-events-none z-0" />;
};

export default ParticleRing;

